import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { StatsCardSkeleton } from "@/components/ui/skeletons";
import {
  BarChart3,
  TrendingUp,
  DollarSign,
  ShoppingCart,
  RefreshCw,
  FileBarChart,
  ChevronDown,
  ChevronRight,
  Package,
  Eye,
} from "lucide-react";
import { useNavigationRefresh } from "@/hooks/useNavigationRefresh";
import apiClient from "@/api/client";
import { useRouter } from "@tanstack/react-router";
import { toastHelpers } from "@/lib/toast-helpers";
import { formatCurrency } from "@/lib/utils";
import type { Order, OrderItem, ReportFilterParams } from "@/types";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { ReportFilters } from "./ReportFilters";
import { useTranslation } from "react-i18next";

export function AdminReports() {
  const { t } = useTranslation();
  const router = useRouter();

  // Auto-refresh data when navigating to this page
  const { manualRefresh, isRefreshing } = useNavigationRefresh([
    "salesReport",
    "ordersReport",
    "incomeReport",
    "analytics",
  ]);

  const [activeTab, setActiveTab] = useState<"analytics" | "reports">(
    "analytics"
  );
  const [selectedPeriod, setSelectedPeriod] = useState<
    "today" | "week" | "month"
  >("today");
  const [expandedOrders, setExpandedOrders] = useState<Set<string>>(new Set());
  const [filters, setFilters] = useState<ReportFilterParams>({
    startDate: "",
    endDate: "",
    orderType: "",
    status: "",
    minAmount: null,
    maxAmount: null,
    searchTerm: "",
  });
  const [activeFilters, setActiveFilters] = useState<string[]>([]);

  const [showPreview, setShowPreview] = useState(false);
  const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
  const [exportLoading, setExportLoading] = useState<"pdf" | "excel" | null>(
    null
  );

  useEffect(() => {
    const decodedToken = apiClient.isAuthenticated();

    if (!decodedToken) {
      toastHelpers.sessionExpired();
      router.navigate({ to: "/login" });
    }
  }, []);

  // Prepare params for query
  const previewParams: ReportFilterParams = {
    ...filters,
    minAmount: filters.minAmount ? Number(filters.minAmount) : undefined,
    maxAmount: filters.maxAmount ? Number(filters.maxAmount) : undefined,
  };

  const { data: analyticsData, isLoading: analyticsLoading } = useQuery({
    queryKey: ["analytics", selectedPeriod],
    queryFn: async () => {
      const res = await apiClient.getAnalytics(selectedPeriod);
      // console.log("Analytics data:", res.data);
      return res.data;
    },
  });

  const {
    data: previewData,
    isLoading: previewLoading,
    refetch: refetchPreview,
  } = useQuery({
    queryKey: ["previewDetails", previewParams],
    queryFn: () =>
      apiClient.getPreviewDetails(previewParams).then((res) => {
        // console.log("Preview data:", res.data);
        return res.data?.data;
      }),
    enabled: false, // Disabled by default, only run on manual trigger
  });

  const clearFilter = (key: keyof ReportFilterParams) => {
    handleFilterChange(key, "");
  };

  const isLoadingAny = isRefreshing || analyticsLoading;

  // Use backend-provided P&L metrics
  const profitLoss = {
    revenue: analyticsData?.totalRevenue?.amount || 0,
    expenses: analyticsData?.totalExpenses?.amount || 0,
    profit: analyticsData?.netProfit?.amount || 0,
    profitMargin:
      analyticsData?.totalRevenue?.amount &&
      analyticsData?.totalRevenue?.amount > 0
        ? ((analyticsData?.netProfit?.amount || 0) /
            analyticsData.totalRevenue.amount) *
          100
        : 0,
    isProfit:
      analyticsData?.netProfit?.isPositive ??
      (analyticsData?.netProfit?.amount || 0) >= 0,
  };

  // small helpers for summary display
  const growthPercentage = analyticsData?.growthRate?.percentage;
  const growthDisplay =
    analyticsData?.growthRate?.formattedPercentage ??
    (growthPercentage != null ? `${growthPercentage}%` : "+0%");
  const growthClass =
    growthPercentage != null && growthPercentage >= 0
      ? "text-green-600"
      : "text-red-600";

  // Function to toggle order expansion
  const toggleOrderExpansion = (orderId: string) => {
    const newExpanded = new Set(expandedOrders);
    if (newExpanded.has(orderId)) {
      newExpanded.delete(orderId);
    } else {
      newExpanded.add(orderId);
    }
    setExpandedOrders(newExpanded);
  };

  const handleFilterChange = (key: keyof ReportFilterParams, value: string) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);

    // Update active filters
    const newActiveFilters = Object.entries(newFilters)
      .filter(([_, val]) => val !== "")
      .map(([key, _]) => key);
    setActiveFilters(newActiveFilters);
  };

  // Handle preview data
  const handlePreviewData = () => {
    setShowPreview(true);
    refetchPreview();
  };

  const formatDateTime = () => {
    const now = new Date();
    const date = now.toISOString().split("T")[0];
    return { date };
  };

  const handleExportPDF = async () => {
    try {
      setExportLoading("pdf");
      const pdfBlob = await apiClient.exportPdfReport(filters);
      const { date } = formatDateTime();
      const filename = `sales_report_${date}.pdf`;
      downloadFile(pdfBlob, filename);
      setExportDropdownOpen(false);
      toastHelpers.success(t("Toast.Reports.PDFExportSuccess"));
    } catch (error) {
      toastHelpers.error(t("Toast.Reports.PDFExportError"));
      console.error("PDF export error:", error);
    } finally {
      setExportLoading(null);
    }
  };

  const downloadFile = (blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleExportExcel = async () => {
    try {
      setExportLoading("excel");
      const excelBlob = await apiClient.exportExcelReport(filters);
      const { date } = formatDateTime();
      const filename = `sales_report_${date}.xlsx`;
      downloadFile(excelBlob, filename);
      setExportDropdownOpen(false);

      toastHelpers.success(t("Toast.Reports.ExcelExportSuccess"));
    } catch (error) {
      toastHelpers.error(t("Toast.Reports.ExcelExportError"));
      console.error("Excel export error:", error);
    } finally {
      setExportLoading(null);
    }
  };

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 transition-all duration-200">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
            {t("AdminReports.Heading")}
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground">
            {t("AdminReports.Subheading")}
          </p>
        </div>

        <div className="flex gap-2 sm:gap-4 w-full sm:w-auto">
          <Button
            variant="outline"
            size="sm"
            onClick={manualRefresh}
            disabled={isRefreshing}
            className="flex items-center gap-2 flex-1 sm:flex-none"
          >
            <RefreshCw className="w-4 h-4" />
            <span className="hidden sm:inline">
              {isRefreshing
                ? t("AdminReports.Refreshing")
                : t("AdminReports.Refresh")}
            </span>
          </Button>
        </div>
      </div>

      {/* Main Tabs */}
      <Tabs
        value={activeTab}
        onValueChange={(value: any) => setActiveTab(value)}
        className="w-full"
      >
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger
            value="analytics"
            className="flex items-center gap-2 text-xs sm:text-sm"
          >
            <BarChart3 className="w-4 h-4" />
            <span className="hidden sm:inline">
              {t("AdminReports.Tabs.Analytics")}
            </span>
          </TabsTrigger>
          <TabsTrigger
            value="reports"
            className="flex items-center gap-2 text-xs sm:text-sm"
          >
            <FileBarChart className="w-4 h-4" />
            <span className="hidden sm:inline">
              {t("AdminReports.Tabs.ReportsExport")}
            </span>
          </TabsTrigger>
        </TabsList>

        {/* Analytics Tab Content */}
        <TabsContent value="analytics" className="space-y-6 mt-6">
          {/* Period Selection for Analytics */}
          <div className="flex gap-2">
            <Button
              variant={selectedPeriod === "today" ? "default" : "outline"}
              size="sm"
              onClick={() => setSelectedPeriod("today")}
            >
              {t("AdminReports.Period.Today")}
            </Button>
            <Button
              variant={selectedPeriod === "week" ? "default" : "outline"}
              size="sm"
              onClick={() => setSelectedPeriod("week")}
            >
              {t("AdminReports.Period.ThisWeek")}
            </Button>
            <Button
              variant={selectedPeriod === "month" ? "default" : "outline"}
              size="sm"
              onClick={() => setSelectedPeriod("month")}
            >
              {t("AdminReports.Period.ThisMonth")}
            </Button>
          </div>

          {/* Period Information */}
          <div className="text-center p-4 bg-muted/30 rounded-lg border">
            <div className="text-sm font-medium text-muted-foreground mb-1">
              {t("AdminReports.AnalyticsPeriod")}: {analyticsData?.period}
            </div>
            <div className="text-lg font-semibold">
              {analyticsData?.dateRange?.start
                ? new Date(analyticsData.dateRange.start).toLocaleDateString(
                    "en-US",
                    {
                      weekday: "short",
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    }
                  )
                : ""}{" "}
              -{" "}
              {analyticsData?.dateRange?.end
                ? new Date(analyticsData.dateRange.end).toLocaleDateString(
                    "en-US",
                    {
                      weekday: "short",
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    }
                  )
                : ""}
            </div>
          </div>

          {/* Analytics Dashboard - Summary Stats */}
          <div className="relative">
            <div
              className={isLoadingAny ? "pointer-events-none opacity-50" : ""}
            >
              {isLoadingAny ? (
                <div className="grid gap-4 md:grid-cols-4">
                  {Array.from({ length: 4 }, (_, i) => (
                    <StatsCardSkeleton key={i} />
                  ))}
                </div>
              ) : (
                <div className="grid gap-4 md:grid-cols-4">
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">
                        {t("AdminReports.Cards.TotalRevenue")}
                      </CardTitle>
                      <DollarSign className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">
                        {formatCurrency(
                          analyticsData?.totalRevenue?.amount || 0
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {analyticsData?.totalRevenue?.comparisonText +
                          " " +
                          t("AdminReports.Cards.PercentageFromPrevious") +
                          " " +
                          selectedPeriod}
                      </p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">
                        {t("AdminReports.Cards.CompletedCancelledOrders")}
                      </CardTitle>
                      <ShoppingCart className="h-4 w-4 text-black" />
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-col gap-2">
                        <div className="text-xl font-bold">
                          {analyticsData?.completedOrders || 0}
                          <span className="text-xs text-muted-foreground ml-2">
                            {t("AdminReports.Cards.Completed")}
                          </span>
                        </div>
                        <div className="text-xl font-bold text-red-600">
                          {analyticsData?.cancelledOrders || 0}
                          <span className="text-xs text-muted-foreground ml-2">
                            {t("AdminReports.Cards.Cancelled")}
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">
                        {t("AdminReports.Cards.AverageOrder")}
                      </CardTitle>
                      <BarChart3 className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">
                        {formatCurrency(
                          analyticsData?.averageOrder?.amount || 0
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {t("AdminReports.Cards.PerOrderValue")}
                      </p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">
                        {t("AdminReports.Cards.GrowthRate")}
                      </CardTitle>
                      <TrendingUp className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <div className={`text-2xl font-bold ${growthClass}`}>
                        {growthDisplay}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {t("AdminReports.Cards.ComparedToPrevious")}
                      </p>
                    </CardContent>
                  </Card>
                </div>
              )}
            </div>
          </div>

          {/* Analytics Data Visualization Section */}
          {!isLoadingAny && analyticsData?.data && (
            <div className="space-y-6">
              {/* Sales Details & P&L Summary Section
              <div className="grid gap-4 md:grid-cols-4">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                      <TrendingUp className="w-4 h-4" />
                      Total Revenue
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {formatCurrency(analyticsData?.totalRevenue?.amount || 0)}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {analyticsData?.completedOrders || 0} completed orders
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                      <ShoppingCart className="w-4 h-4" />
                      Total Expenses
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {formatCurrency(profitLoss.expenses)}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Approved expenses only
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                      <DollarSign className="w-4 h-4" />
                      {profitLoss.isProfit ? "Net Profit" : "Net Loss"}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {profitLoss.isProfit ? "+" : ""}
                      {formatCurrency(profitLoss.profit)}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Margin: {profitLoss.profitMargin.toFixed(1)}%
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                      <BarChart3 className="w-4 h-4" />
                      Order Success Rate
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {analyticsData?.completedOrders &&
                      analyticsData?.cancelledOrders &&
                      analyticsData?.completedOrders +
                        analyticsData?.cancelledOrders >
                        0
                        ? (
                            (analyticsData.completedOrders /
                              (analyticsData.completedOrders +
                                analyticsData.cancelledOrders)) *
                            100
                          ).toFixed(1)
                        : "0.0"}
                      %
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {analyticsData?.cancelledOrders || 0} cancelled orders
                    </p>
                  </CardContent>
                </Card>
              </div> */}

              {/* Profit & Loss Section - Only show for monthly period */}
              {selectedPeriod === "month" && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base sm:text-lg font-semibold flex items-center gap-2">
                      <DollarSign className="w-4 h-4 sm:w-5 sm:h-5" />
                      {t("AdminReports.ProfitLoss.Title")}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3 sm:space-y-4">
                      {/* Revenue */}
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 sm:p-3 bg-muted rounded-lg border gap-2 sm:gap-0">
                        <div className="flex items-center gap-2">
                          <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4 text-muted-foreground flex-shrink-0" />
                          <span className="font-medium text-sm sm:text-base">
                            {t("AdminReports.ProfitLoss.TotalRevenue")}
                          </span>
                        </div>
                        <span className="text-base sm:text-lg font-bold pl-5 sm:pl-0">
                          {formatCurrency(profitLoss.revenue)}
                        </span>
                      </div>

                      {/* Expenses */}
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 sm:p-3 bg-muted rounded-lg border gap-2 sm:gap-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <ShoppingCart className="w-3 h-3 sm:w-4 sm:h-4 text-muted-foreground flex-shrink-0" />
                          <span className="font-medium text-sm sm:text-base">
                            {t("AdminReports.ProfitLoss.TotalExpenses")}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {t("AdminReports.ProfitLoss.ApprovedOnly")}
                          </span>
                        </div>
                        <span className="text-base sm:text-lg font-bold pl-5 sm:pl-0">
                          -{formatCurrency(profitLoss.expenses)}
                        </span>
                      </div>

                      {/* Divider */}
                      <div className="border-t-2 border-gray-300 my-2"></div>

                      {/* Net Profit/Loss */}
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 sm:p-4 bg-muted rounded-lg border-2 gap-2 sm:gap-0">
                        <div>
                          <span className="text-base sm:text-lg font-bold">
                            {profitLoss.isProfit
                              ? t("AdminReports.ProfitLoss.NetProfit")
                              : t("AdminReports.ProfitLoss.NetLoss")}
                          </span>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs sm:text-sm text-muted-foreground">
                              {t("AdminReports.ProfitLoss.ProfitMargin")}:
                            </span>
                            <span className="font-semibold text-sm sm:text-base">
                              {profitLoss.profitMargin.toFixed(2)}%
                            </span>
                          </div>
                        </div>
                        <span className="text-xl sm:text-2xl font-bold">
                          {profitLoss.isProfit ? "+" : ""}
                          {formatCurrency(profitLoss.profit)}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Sales Breakdown by Order Type */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base sm:text-lg font-semibold flex items-center gap-2">
                    <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5" />
                    {t("AdminReports.SalesBreakdown.Title")}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 sm:space-y-4">
                    {(() => {
                      const ordersByType = (analyticsData?.data || []).reduce(
                        (acc: any, order: Order) => {
                          const type =
                            order.order_type ||
                            t("AdminReports.Common.Unknown");
                          if (!acc[type]) {
                            acc[type] = {
                              count: 0,
                              revenue: 0,
                            };
                          }
                          acc[type].count++;
                          const orderRevenue = Number(
                            order.price || order.subtotal || 0
                          );
                          acc[type].revenue += orderRevenue;
                          return acc;
                        },
                        {}
                      );

                      // Calculate total for verification
                      const totalRevenue = Object.values(ordersByType).reduce(
                        (sum: number, data: any) => sum + data.revenue,
                        0
                      );

                      return (
                        <>
                          {Object.entries(ordersByType).map(
                            ([type, data]: [string, any]) => {
                              const percentage =
                                totalRevenue > 0
                                  ? (data.revenue / totalRevenue) * 100
                                  : 0;

                              return (
                                <div key={type} className="space-y-2">
                                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <Badge
                                        variant={
                                          type === "dine_in"
                                            ? "default"
                                            : type === "delivery"
                                              ? "secondary"
                                              : "outline"
                                        }
                                        className="text-xs"
                                      >
                                        {type.replace("_", " ").toUpperCase()}
                                      </Badge>
                                      <span className="text-xs sm:text-sm text-muted-foreground">
                                        {data.count}{" "}
                                        {data.count === 1
                                          ? t(
                                              "AdminReports.SalesBreakdown.Order"
                                            )
                                          : t(
                                              "AdminReports.SalesBreakdown.Orders"
                                            )}
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-2 pl-0 sm:pl-0">
                                      <span className="text-xs text-muted-foreground">
                                        {percentage.toFixed(1)}%
                                      </span>
                                      <span className="font-semibold text-sm sm:text-base">
                                        {formatCurrency(data.revenue)}
                                      </span>
                                    </div>
                                  </div>
                                  <div className="w-full bg-muted rounded-full h-2">
                                    <div
                                      className="bg-primary h-2 rounded-full transition-all"
                                      style={{
                                        width: `${percentage}%`,
                                      }}
                                    />
                                  </div>
                                </div>
                              );
                            }
                          )}
                        </>
                      );
                    })()}
                  </div>
                </CardContent>
              </Card>

              {/* Top Products by Revenue */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base sm:text-lg font-semibold flex items-center gap-2">
                    <Package className="w-4 h-4 sm:w-5 sm:h-5" />
                    {t("AdminReports.TopProducts.Title")}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 sm:space-y-4">
                    {(() => {
                      const productRevenue: {
                        [key: string]: {
                          name: string;
                          revenue: number;
                          quantity: number;
                        };
                      } = {};

                      (analyticsData?.data || []).forEach((order: Order) => {
                        (order.OrderItems || []).forEach((item: OrderItem) => {
                          const productName =
                            item.Stock?.name ||
                            t("AdminReports.Common.Unknown");
                          const itemRevenue = Number(item.price || 0);
                          const itemQuantity = Number(item.quantity || 0);

                          if (!productRevenue[productName]) {
                            productRevenue[productName] = {
                              name: productName,
                              revenue: 0,
                              quantity: 0,
                            };
                          }
                          productRevenue[productName].revenue += itemRevenue;
                          productRevenue[productName].quantity += itemQuantity;
                        });
                      });

                      const topProducts = Object.values(productRevenue)
                        .sort((a, b) => b.revenue - a.revenue)
                        .slice(0, 5);

                      const maxRevenue = topProducts[0]?.revenue || 1;

                      return topProducts.length > 0 ? (
                        <>
                          {topProducts.map((product, index) => {
                            const percentage =
                              (product.revenue / maxRevenue) * 100;

                            return (
                              <div key={index} className="space-y-2">
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-2">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-xs sm:text-sm font-medium">
                                      {product.name}
                                    </span>
                                    <span className="text-xs text-muted-foreground">
                                      ({product.quantity}{" "}
                                      {t("AdminReports.TopProducts.Sold")})
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs text-muted-foreground">
                                      {percentage.toFixed(1)}%
                                    </span>
                                    <span className="font-semibold text-sm sm:text-base">
                                      {formatCurrency(product.revenue)}
                                    </span>
                                  </div>
                                </div>
                                <div className="w-full bg-muted rounded-full h-2">
                                  <div
                                    className="bg-primary h-2 rounded-full transition-all"
                                    style={{
                                      width: `${percentage}%`,
                                    }}
                                  />
                                </div>
                              </div>
                            );
                          })}
                        </>
                      ) : (
                        <p className="text-xs sm:text-sm text-muted-foreground text-center py-4">
                          {t("AdminReports.TopProducts.NoData")}
                        </p>
                      );
                    })()}
                  </div>
                </CardContent>
              </Card>

              {/* Orders Table (Analytics Tab) */}
              <div className="border rounded-lg overflow-hidden">
                <div className="p-3 sm:p-4 bg-muted/50 font-medium text-sm border-b">
                  <h3 className="text-base sm:text-lg font-semibold flex items-center gap-2">
                    <FileBarChart className="w-4 h-4 sm:w-5 sm:h-5" />
                    {t("AdminReports.OrdersTable.Title")} (
                    {analyticsData.data?.length || 0})
                  </h3>
                </div>
                <div className="relative overflow-x-auto">
                  <div className="max-h-[600px] overflow-y-auto">
                    <div className="grid grid-cols-7 gap-4 p-3 bg-white border-b font-medium text-xs sm:text-sm sticky top-0 z-10 shadow-sm min-w-[800px]">
                      <div className="text-left">
                        {t("AdminReports.OrdersTable.Headers.OrderID")}
                      </div>
                      <div className="text-center">
                        {t("AdminReports.OrdersTable.Headers.Type")}
                      </div>
                      <div className="text-center">
                        {t("AdminReports.OrdersTable.Headers.Status")}
                      </div>
                      <div className="text-center">
                        {t("AdminReports.OrdersTable.Headers.Items")}
                      </div>
                      <div className="text-center">
                        {t("AdminReports.OrdersTable.Headers.Amount")}
                      </div>
                      <div className="text-center">
                        {t("AdminReports.OrdersTable.Headers.DateTime")}
                      </div>
                      <div className="text-center">
                        {t("AdminReports.OrdersTable.Headers.Actions")}
                      </div>
                    </div>
                    {analyticsData.data && analyticsData.data.length > 0 ? (
                      analyticsData.data.map((order: Order, index: number) => {
                        const orderId = order.id || `order-${index}`;
                        const isExpanded = expandedOrders.has(orderId);

                        return (
                          <div key={orderId}>
                            {/* Main Order Row */}
                            <div className="grid grid-cols-7 gap-4 p-3 border-b text-xs sm:text-sm hover:bg-muted/20 transition-colors bg-white min-w-[800px]">
                              <div className="font-mono text-xs text-left">
                                #{(order.id || "N/A").toString()}
                              </div>
                              <div className="text-center">
                                <Badge
                                  variant={
                                    order.order_type === "dine_in"
                                      ? "default"
                                      : order.order_type === "delivery"
                                        ? "secondary"
                                        : "outline"
                                  }
                                  className="text-xs"
                                >
                                  {order.order_type?.replace("_", " ") || "N/A"}
                                </Badge>
                              </div>
                              <div className="text-center">
                                <Badge
                                  variant={
                                    order.status === "completed"
                                      ? "default"
                                      : order.status === "preparing"
                                        ? "secondary"
                                        : order.status === "cancelled"
                                          ? "destructive"
                                          : "outline"
                                  }
                                  className="text-xs"
                                >
                                  {order.status || "N/A"}
                                </Badge>
                              </div>
                              <div className="text-center font-medium">
                                {order.OrderItems?.length || "N/A"}
                              </div>
                              <div className="text-center font-bold">
                                {formatCurrency(
                                  order.price || order.subtotal || 0
                                )}
                              </div>
                              <div className="text-center text-xs">
                                <div>
                                  {new Date(
                                    order.created_at
                                  ).toLocaleDateString()}
                                </div>
                                <div className="text-muted-foreground">
                                  {new Date(
                                    order.created_at
                                  ).toLocaleTimeString([], {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })}
                                </div>
                              </div>
                              <div className="text-center">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => toggleOrderExpansion(orderId)}
                                  className="h-6 w-6 p-0"
                                  disabled={
                                    !order.OrderItems ||
                                    order.OrderItems.length === 0
                                  }
                                >
                                  {isExpanded ? (
                                    <ChevronDown className="h-4 w-4" />
                                  ) : (
                                    <ChevronRight className="h-4 w-4" />
                                  )}
                                </Button>
                              </div>
                            </div>

                            {/* Expanded Order Items */}
                            {isExpanded &&
                              order.OrderItems &&
                              order.OrderItems.length > 0 && (
                                <div className="bg-muted/10 border-b">
                                  <div className="p-4">
                                    <div className="flex items-center gap-2 mb-3">
                                      <Package className="h-4 w-4 text-muted-foreground" />
                                      <h5 className="text-sm font-medium">
                                        {t("AdminReports.OrderItems.Title")}
                                      </h5>
                                    </div>
                                    <div className="space-y-2">
                                      {order.OrderItems.map(
                                        (item, itemIndex) => (
                                          <div
                                            key={item.id || itemIndex}
                                            className="grid grid-cols-4 gap-4 text-xs p-2 bg-white rounded border"
                                          >
                                            <div className="font-medium">
                                              {item.Stock?.name ||
                                                t(
                                                  "AdminReports.OrderItems.UnknownItem"
                                                )}
                                            </div>
                                            <div className="text-center">
                                              {t("AdminReports.OrderItems.Qty")}
                                              : {item.quantity}
                                            </div>
                                            <div className="text-center">
                                              {formatCurrency(
                                                item.Stock?.cost_per_unit || 0
                                              )}{" "}
                                              {t(
                                                "AdminReports.OrderItems.Each"
                                              )}
                                            </div>
                                            <div className="text-right font-medium">
                                              {formatCurrency(item.price || 0)}
                                            </div>
                                          </div>
                                        )
                                      )}
                                    </div>
                                    {order.notes && (
                                      <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded">
                                        <div className="text-xs">
                                          <span className="font-medium text-yellow-800">
                                            {t("AdminReports.OrderItems.Notes")}
                                            :{" "}
                                          </span>
                                          <span className="text-yellow-700">
                                            {order.notes}
                                          </span>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                          </div>
                        );
                      })
                    ) : (
                      <div className="p-8 text-center text-gray-500">
                        <div className="flex flex-col items-center gap-3">
                          <FileBarChart className="w-12 h-12 text-gray-400" />
                          <div>
                            <h3 className="text-lg font-medium text-gray-700 mb-1">
                              {t("AdminReports.EmptyState.NoOrdersFound")}
                            </h3>
                            <p className="text-sm">
                              {t("AdminReports.EmptyState.NoOrderData")}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </TabsContent>

        {/* Reports Tab Content */}
        <TabsContent value="reports" className="space-y-6 mt-6">
          {/* Report Filters */}
          <ReportFilters
            handleFilterChange={handleFilterChange}
            filters={filters}
            onPreviewData={handlePreviewData}
            activeFilters={activeFilters}
            clearFilter={clearFilter}
            loading={previewLoading}
            totalRecords={previewData?.length || 0}
            handleExportPDF={handleExportPDF}
            handleExportExcel={handleExportExcel}
            exportLoading={exportLoading}
            exportDropdownOpen={exportDropdownOpen}
            setExportDropdownOpen={setExportDropdownOpen}
            onRemoveAllFilters={() => {
              setFilters({
                startDate: "",
                endDate: "",
                orderType: "",
                status: "",
                minAmount: null,
                maxAmount: null,
                searchTerm: "",
              });
              setActiveFilters([]);
            }}
          />

          {/* Preview Section */}
          {showPreview && previewData && (
            <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6 mb-6 border">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mb-4">
                <div className="flex items-center gap-2">
                  <Eye className="w-5 h-5 text-blue-600" />
                  <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
                    {t("AdminReports.Preview.Title")}
                  </h2>
                </div>
                <span className="text-sm text-gray-500">
                  ({previewData ? previewData.length : 0}{" "}
                  {t("AdminReports.Preview.RecordsFound")})
                </span>
              </div>

              {/* Quick Stats for Filtered Data */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-blue-600 text-sm font-medium">
                        {t("AdminReports.Preview.FilteredOrders")}
                      </p>
                      <p className="text-2xl font-bold text-blue-900">
                        {previewData.length}
                      </p>
                    </div>
                    <ShoppingCart className="w-8 h-8 text-blue-600" />
                  </div>
                </div>
              </div>

              <p className="text-sm text-gray-600 mb-4">
                {t("AdminReports.Preview.Description")}
              </p>
            </div>
          )}

          {/* Detailed Orders Table for Reports */}
          {previewData && (
            <div className="border rounded-lg overflow-hidden">
              <div className="p-4 bg-muted/50 font-medium text-sm border-b">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <FileBarChart className="w-5 h-5" />
                  {t("AdminReports.DetailedOrders.Title")} (
                  {previewData.length || 0}{" "}
                  {t("AdminReports.DetailedOrders.Orders")})
                </h3>
              </div>
              <div className="relative">
                <div className="max-h-[600px] overflow-y-auto">
                  <div className="grid grid-cols-7 gap-4 p-3 bg-white border-b font-medium text-sm sticky top-0 z-10 shadow-sm">
                    <div className="text-left">
                      {t("AdminReports.OrdersTable.Headers.OrderID")}
                    </div>
                    <div className="text-center">
                      {t("AdminReports.OrdersTable.Headers.Type")}
                    </div>
                    <div className="text-center">
                      {t("AdminReports.OrdersTable.Headers.Status")}
                    </div>
                    <div className="text-center">
                      {t("AdminReports.OrdersTable.Headers.Items")}
                    </div>
                    <div className="text-center">
                      {t("AdminReports.OrdersTable.Headers.Amount")}
                    </div>
                    <div className="text-center">
                      {t("AdminReports.OrdersTable.Headers.DateTime")}
                    </div>
                    <div className="text-center">
                      {t("AdminReports.OrdersTable.Headers.Actions")}
                    </div>
                  </div>
                  {previewData && previewData.length > 0 ? (
                    previewData.map((order: Order, index: number) => {
                      const orderId = order.id || `order-${index}`;
                      const isExpanded = expandedOrders.has(orderId);

                      return (
                        <div key={orderId}>
                          {/* Main Order Row */}
                          <div className="grid grid-cols-7 gap-4 p-3 border-b text-sm hover:bg-muted/20 transition-colors bg-white">
                            <div className="font-mono text-xs text-left">
                              #{(order.id || "N/A").toString()}
                            </div>
                            <div className="text-center">
                              <Badge
                                variant={
                                  order.order_type === "dine_in"
                                    ? "default"
                                    : order.order_type === "delivery"
                                      ? "secondary"
                                      : "outline"
                                }
                              >
                                {order.order_type?.replace("_", " ") || "N/A"}
                              </Badge>
                            </div>
                            <div className="text-center">
                              <Badge
                                variant={
                                  order.status === "completed"
                                    ? "default"
                                    : order.status === "preparing"
                                      ? "secondary"
                                      : order.status === "cancelled"
                                        ? "destructive"
                                        : "outline"
                                }
                              >
                                {order.status || "N/A"}
                              </Badge>
                            </div>
                            <div className="text-center font-medium">
                              {order.OrderItems?.length || "N/A"}
                            </div>
                            <div className="text-center font-bold">
                              {formatCurrency(
                                order.price || order.subtotal || 0
                              )}
                            </div>
                            <div className="text-center text-xs">
                              <div>
                                {new Date(
                                  order.created_at
                                ).toLocaleDateString()}
                              </div>
                              <div className="text-muted-foreground">
                                {new Date(order.created_at).toLocaleTimeString(
                                  [],
                                  {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  }
                                )}
                              </div>
                            </div>
                            <div className="text-center">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => toggleOrderExpansion(orderId)}
                                className="h-6 w-6 p-0"
                                disabled={
                                  !order.OrderItems ||
                                  order.OrderItems.length === 0
                                }
                              >
                                {isExpanded ? (
                                  <ChevronDown className="h-4 w-4" />
                                ) : (
                                  <ChevronRight className="h-4 w-4" />
                                )}
                              </Button>
                            </div>
                          </div>

                          {/* Expanded Order Items */}
                          {isExpanded &&
                            order.OrderItems &&
                            order.OrderItems.length > 0 && (
                              <div className="bg-muted/10 border-b">
                                <div className="p-4">
                                  <div className="flex items-center gap-2 mb-3">
                                    <Package className="h-4 w-4 text-muted-foreground" />
                                    <h5 className="text-sm font-medium">
                                      {t("AdminReports.OrderItems.Title")}
                                    </h5>
                                  </div>
                                  <div className="space-y-2">
                                    {order.OrderItems.map((item, itemIndex) => (
                                      <div
                                        key={item.id || itemIndex}
                                        className="grid grid-cols-4 gap-4 text-xs p-2 bg-white rounded border"
                                      >
                                        <div className="font-medium">
                                          {item.Stock?.name ||
                                            t(
                                              "AdminReports.OrderItems.UnknownItem"
                                            )}
                                        </div>
                                        <div className="text-center">
                                          {t("AdminReports.OrderItems.Qty")}:{" "}
                                          {item.quantity}
                                        </div>
                                        <div className="text-center">
                                          {formatCurrency(
                                            item.Stock?.cost_per_unit || 0
                                          )}{" "}
                                          {t("AdminReports.OrderItems.Each")}
                                        </div>
                                        <div className="text-right font-medium">
                                          {formatCurrency(item.price || 0)}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                  {order.notes && (
                                    <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded">
                                      <div className="text-xs">
                                        <span className="font-medium text-yellow-800">
                                          {t("AdminReports.OrderItems.Notes")}
                                          :{" "}
                                        </span>
                                        <span className="text-yellow-700">
                                          {order.notes}
                                        </span>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                        </div>
                      );
                    })
                  ) : (
                    <div className="p-8 text-center text-gray-500">
                      <div className="flex flex-col items-center gap-3">
                        <FileBarChart className="w-12 h-12 text-gray-400" />
                        <div>
                          <h3 className="text-lg font-medium text-gray-700 mb-1">
                            {t("AdminReports.EmptyState.NoOrdersFound")}
                          </h3>
                          <p className="text-sm">
                            {t("AdminReports.EmptyState.NoOrderData")}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
